import React, { useContext, useState, useEffect, useCallback } from "react";
import { TranslatorContext } from "../../context/Translator";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Row, Col, Spinner, Alert, Form, Button } from "react-bootstrap";
import Select from "react-select";
import axios from "axios";
import { FloatCardComponent } from "../../components/cards";
import ProductCreatorProductsTableComponent from "../../components/tables/productcreator/ProductCreatorProductsTableComponent";
import LabelFieldComponent from "../../components/fields/LabelFieldComponent";
import PageLayout from "../../layouts/PageLayout";

export default function ProductCreatorProductListPage() {
    const { t, n } = useContext(TranslatorContext);
    const location = useLocation();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState("added");

    const [allProducts, setAllProducts] = useState([]);
    const [productsList, setProductsList] = useState([]);
    const [selectedProductIds, setSelectedProductIds] = useState([]);

    const [categoriesOptions, setCategoriesOptions] = useState([]);
    const [brandsOptions, setBrandsOptions] = useState([]);
    const [storesOptions, setStoresOptions] = useState([]);

    const [searchTerm, setSearchTerm] = useState("");
    const [selectedCategory, setSelectedCategory] = useState("");
    const [selectedBrand, setSelectedBrand] = useState("");
    const [selectedStore, setSelectedStore] = useState("");

    const [counts, setCounts] = useState({
        added: 0,
    });

    const tableHeaders = [
        "Product Code",
        "Title",
        "Detail",
        "Seller Price",
        "Action",
    ];

    const getAuthHeader = () => ({
        headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
    });

    const extractData = (res) => {
        if (res?.data?.products && Array.isArray(res.data.products)) return res.data.products;
        if (res?.data?.data && Array.isArray(res.data.data)) return res.data.data;
        if (Array.isArray(res?.data)) return res.data;
        return [];
    };

    const fetchInitialFilters = useCallback(async () => {
        try {
            const baseUrl = process.env.REACT_APP_API_URL;
            const response = await axios.get(`${baseUrl}/product-creator/initial-data`, getAuthHeader());
            const resData = response.data?.data || response.data;

            if (resData?.product_categories) {
                const flattenedCategories = [];
                resData.product_categories.forEach((cat) => {
                    if (cat.child_categories && cat.child_categories.length > 0) {
                        cat.child_categories.forEach((child) => {
                            flattenedCategories.push({
                                id: child.id,
                                name: `${cat.name} > ${child.name}`,
                                nickname: child.nickname || child.nick_name || cat.nickname || cat.nick_name,
                            });
                        });
                    } else {
                        flattenedCategories.push({
                            id: cat.id,
                            name: cat.name,
                            nickname: cat.nickname || cat.nick_name,
                        });
                    }
                });
                setCategoriesOptions(flattenedCategories);
            }

            if (resData?.brands) setBrandsOptions(resData.brands);
            if (resData?.stores) setStoresOptions(resData.stores);
        } catch (err) {
            console.error("Error fetching filter options:", err);
        }
    }, []);

    const fetchTabData = useCallback(async (tab) => {
        setLoading(true);
        setError(null);
        setSelectedProductIds([]);

        let endpoint = "/product-creator/products";

        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}${endpoint}`,
                getAuthHeader()
            );

            const listData = extractData(response);
            setAllProducts(listData);
            setProductsList(listData);
            setCounts({ added: listData.length });
        } catch (err) {
            console.error("Error fetching tab data:", err);
            setError(
                err.response?.data?.message || "Failed to fetch products. Please try again."
            );
            setAllProducts([]);
            setProductsList([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const tabParam = queryParams.get("tab");
        if (tabParam) {
            setActiveTab(tabParam);
        }
    }, [location.search]);

    useEffect(() => {
        fetchInitialFilters();
    }, [fetchInitialFilters]);

    useEffect(() => {
        fetchTabData(activeTab);
    }, [activeTab, fetchTabData]);

    useEffect(() => {
        let filtered = [...allProducts];

        if (searchTerm.trim()) {
            const query = searchTerm.toLowerCase().trim();
            filtered = filtered.filter(
                (p) =>
                    (p.title && p.title.toLowerCase().includes(query)) ||
                    (p.product_code && p.product_code.toLowerCase().includes(query))
            );
        }

        if (selectedCategory) {
            filtered = filtered.filter(
                (p) => String(p.category_id || p.category?.id) === String(selectedCategory)
            );
        }

        if (selectedBrand) {
            filtered = filtered.filter(
                (p) => String(p.brand_id || p.brand?.id) === String(selectedBrand)
            );
        }

        if (selectedStore) {
            filtered = filtered.filter(
                (p) => String(p.store_id || p.store?.id) === String(selectedStore)
            );
        }

        setProductsList(filtered);
    }, [allProducts, searchTerm, selectedCategory, selectedBrand, selectedStore]);

    const handleTabChange = (tabKey) => {
        setActiveTab(tabKey);
        navigate(`/product-creator/products?tab=${tabKey}`);
    };

    const formatSelectOptions = (list) =>
        list.map((item) => {
            const name = item.name || item.title || "";
            const nick = item.nickname || item.nick_name;
            const label = nick ? `${name} (${nick})` : name;

            return {
                value: String(item.id),
                label: label,
            };
        });

    const customSelectStyles = {
        control: (provided) => ({
            ...provided,
            minHeight: "38px",
            borderRadius: "0.375rem",
            borderColor: "#dee2e6",
        }),
    };

    const cardsData = [
        {
            key: "added",
            title: "Added Products",
            digit: counts.added,
            icon: "shopping_bag",
            variant: activeTab === "added" ? "lg blue active-card" : "lg blue",
        },
    ];

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <div className="mc-card d-flex flex-wrap justify-content-between align-items-center">
                        <div className="mc-breadcrumb mb-0">
                            <h3 className="mc-breadcrumb-title">
                                {t("product_list")} ({productsList.length})
                            </h3>
                            <ul className="mc-breadcrumb-list">
                                <li className="mc-breadcrumb-item">
                                    <Link to="#" className="mc-breadcrumb-link">
                                        {t("home")}
                                    </Link>
                                </li>
                                <li className="mc-breadcrumb-item">
                                    <Link to="#" className="mc-breadcrumb-link">
                                        {t("products")}
                                    </Link>
                                </li>
                                <li className="mc-breadcrumb-item">{t("product_list")}</li>
                            </ul>
                        </div>
                        <div>
                            <Button
                                variant="primary"
                                onClick={() => navigate("/product-creator/products/create")}
                                className="d-flex align-items-center gap-1"
                            >
                                <span className="material-icons">add</span>
                                {t("create_product")}
                            </Button>
                        </div>
                    </div>
                </Col>

                {cardsData.map((card) => (
                    <Col key={card.key} className="mb-3">
                        <div
                            style={{ cursor: "pointer" }}
                            onClick={() => handleTabChange(card.key)}
                        >
                            <FloatCardComponent
                                variant={card.variant}
                                digit={n ? n(card.digit) : card.digit}
                                title={card.title}
                                icon={card.icon}
                            />
                        </div>
                    </Col>
                ))}

                <Col xl={12}>
                    <div className="mc-card">
                        {error && (
                            <Alert variant="danger" className="mb-4">
                                {error}
                            </Alert>
                        )}

                        <Row className="mb-4 g-3 align-items-end">
                            <Col xs={12} sm={6} md={6}>
                                <LabelFieldComponent
                                    type="search"
                                    placeholder={t("search by title or code")}
                                    label={t("search")}
                                    labelDir="label-col"
                                    fieldSize="w-100 h-md"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </Col>

                           

                            {storesOptions.length > 0 && (
                                <Col xs={12} sm={6} md={3}>
                                    <Form.Group>
                                        <Form.Label className="mc-label-field-title mb-2">
                                            {t("store by")}
                                        </Form.Label>
                                        <Select
                                            isClearable
                                            isSearchable
                                            placeholder="All Stores"
                                            styles={customSelectStyles}
                                            options={formatSelectOptions(storesOptions)}
                                            value={
                                                formatSelectOptions(storesOptions).find(
                                                    (opt) => opt.value === selectedStore
                                                ) || null
                                            }
                                            onChange={(opt) => setSelectedStore(opt ? opt.value : "")}
                                        />
                                    </Form.Group>
                                </Col>
                            )}
                        </Row>

                        <Row>
                            <Col xl={12}>
                                {loading ? (
                                    <div className="text-center py-5">
                                        <Spinner animation="border" variant="primary" />
                                        <p className="mt-2 text-muted">{t("loading_products...")}</p>
                                    </div>
                                ) : (
                                    <ProductCreatorProductsTableComponent
                                        thead={tableHeaders}
                                        tbody={productsList}
                                    />
                                )}
                            </Col>
                        </Row>
                    </div>
                </Col>
            </Row>
        </PageLayout>
    );
}
